import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { dimGrey, inactive, lightBlack, lightTeal } from '../../mainTheme';
import { setFacilitiesBreadcrumbs } from '../../store/facility/actionCreators';
import {
  fetchPharmacyPatients,
  fetchPharmacyUploads,
} from '../../store/pharmacyPatients/actionCreators';
import { parseDateToLocal } from '../../shared/utils';
import PatientDetailTab from './components/PatientDetailTab';
import MedicationUploadTab from './components/MedicationUploadTab';
import MedicationUploadModal from './components/MedicationUploadModal';

const tabStyles = {
  indicator: {
    backgroundColor: lightTeal,
  },
  tab: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    color: dimGrey,
    lineHeight: '20px',
    letterSpacing: '0.24px',
  },
  selected: {
    color: `${lightBlack} !important`,
  },
};

const TAB_LABELS = ['Patient detail', 'Medication upload'];
const MEDICATION_TAB_INDEX = 1;

const PharmacyPatientDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const patientId = Number(id);

  const { patients, uploads, isUploadsLoading } = useSelector(
    (state: RootState) => state.pharmacyPatients,
  );
  const patient = useMemo(
    () => patients.find((p) => p.patient_customer_id === patientId),
    [patients, patientId],
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Load the patient list if the user deep-linked straight to a detail page.
  useEffect(() => {
    if (!patients?.length) {
      dispatch(fetchPharmacyPatients());
    }
  }, [dispatch, patients?.length]);

  // Load this patient's medication upload history.
  useEffect(() => {
    if (Number.isFinite(patientId)) {
      dispatch(fetchPharmacyUploads(patientId));
    }
  }, [dispatch, patientId]);

  // Topbar breadcrumbs
  useEffect(() => {
    const crumbs = [{ label: 'Patients', path: '/v2/pharmacy/patients' }];
    if (patient) {
      crumbs.push({
        label:
          `${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
          'Patient',
        path: `/v2/pharmacy/patients/${patientId}`,
      });
    }
    dispatch(setFacilitiesBreadcrumbs(crumbs));
    return () => {
      dispatch(setFacilitiesBreadcrumbs([]));
    };
  }, [dispatch, patient, patientId]);

  const handleUploadComplete = () => {
    setSelectedTab(MEDICATION_TAB_INDEX);
    dispatch(fetchPharmacyUploads(patientId));
  };

  const patientName = patient
    ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
    : 'N/A';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
      <Box display="flex" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            border: `1px solid ${inactive}`,
            borderRadius: '10px',
            width: '60px',
            height: '40px',
            '&:hover': { border: `1px solid ${inactive}` },
            marginBottom: '24px',
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
          onClick={() => setIsUploadModalOpen(true)}
          sx={{ height: '40px' }}
        >
          Upload new medication
        </Button>
      </Box>

      <Box p="12px" borderRadius="8px" border={`1px solid ${inactive}`}>
        <Box display="flex" gap={2}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: '#555' }}>
            {patient?.first_name?.charAt(0)}
          </Avatar>
          <Box display="flex" flexDirection="column" gap="8px" width="100%">
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '22px',
                lineHeight: '30px',
                letterSpacing: '0.36px',
                color: lightBlack,
              }}
            >
              {patientName}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: dimGrey,
              }}
            >
              Patient Pharmacy ID: {patient?.pharmacy_patient_id || 'N/A'}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: dimGrey,
              }}
            >
              Age {patient?.age ?? 'N/A'} ·{' '}
              {[patient?.city, patient?.state].filter(Boolean).join(', ') ||
                'N/A'}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.2px',
                color: dimGrey,
              }}
            >
              Created{' '}
              {patient?.created_at
                ? parseDateToLocal(patient.created_at, 'MMM dd, yyyy')
                : 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTabs-indicator': tabStyles.indicator,
          '& .MuiTab-root': tabStyles.tab,
          '& .Mui-selected': tabStyles.selected,
          marginTop: '16px',
        }}
      >
        {TAB_LABELS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>
      <Box sx={{ borderBottom: 1, borderColor: inactive }} />

      <Box mt={2}>
        {selectedTab === 0 && <PatientDetailTab patient={patient} />}
        {selectedTab === MEDICATION_TAB_INDEX && (
          <MedicationUploadTab
            uploads={uploads}
            loading={isUploadsLoading}
            onUploadClick={() => setIsUploadModalOpen(true)}
          />
        )}
      </Box>

      {isUploadModalOpen && (
        <MedicationUploadModal
          patientId={patientId}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </Box>
  );
};

export default PharmacyPatientDetails;
